import ReferenceIcon from '@assets/icons/note.svg?react'
import RecipientIcon from '@assets/icons/user.svg?react'
import {
  CoreApplicationFeeAnnounce,
  CoreInput,
  CoreOnboardingAnnounce,
  CorePaymentInput,
  CoreSelect,
  MMButton,
  SendMoneyMethodInfo,
  SuccessPane
} from '@components'
import { useHeaderContext } from '@context/HeaderContext'
import { extendedSelectOptions } from '@utils'
import { WalletRecipientEdit } from '@views/recipients/elements/WalletRecipientEdit'
import AccountingCategorySelect from '@withdrawals/components/AccountingCategorySelect'
import styles from '@withdrawals/styles/WithdrawInitiate.module.scss'
import WalletTransferPreview from '@withdrawals/views/WalletTransferPreview'
import {
  ExternalWalletTypes,
  MerchantAccountTypes,
  PaymentOptionStatuses,
  responseDoesNotContainsChallenge,
  selectMerchantAccountType,
  useAppSelector,
  useReturnToOrigin,
  useShouldPayApplicationFeeQry,
  useWalletTransferQry,
  WithdrawalSubFlows
} from 'mmfintech-backend-api'
import { GlobalContext, isValidArray, isValidObject, OtpContext, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

const JetonWalletTransfer = () => {
  const { modalHide, modalShow } = useContext(GlobalContext)
  const { setOtpOnSuccess } = useContext(OtpContext)

  const { currentStep, setCurrentStep } = useHeaderContext()

  const [result, setResult] = useState(null)
  const [promoCode, setPromoCode] = useState('')

  const history = useHistory()

  const accountType = useAppSelector(selectMerchantAccountType)
  const shouldPayApplicationFee = useShouldPayApplicationFeeQry()

  const { returnToOrigin } = useReturnToOrigin()
  const returnToOriginUrl = returnToOrigin()

  const {
    formValues,
    handleRecipientChange,
    handlePreview,
    handleInitiate,
    reloadRecipients,
    selectedAccount,
    selectedRecipient,
    setSelectedAccount,
    filteredRecipients,
    shouldShowWarning,
    accounts,
    accountsError,
    accountsFetching,
    recipientsError,
    recipientsFetching,
    previewFetching,
    previewError,
    resetMutations
  } = useWalletTransferQry({
    flowType: WithdrawalSubFlows.JETON_WALLET, // was 'KINGDOM_WALLET' ?!?
    editRecipient: () => handleEditRecipient(),
    onPreviewSuccess: () => setCurrentStep(2),
    onInitiateSuccess: (data: any) => handleInitiateSuccess(data),
    selectAddNew: false
  })

  const handleEditRecipient = (recipient?: any): void => {
    const { currencyCode, supportedCountries } = selectedAccount || {}
    if (currencyCode) {
      modalShow({
        header: recipient?.paymentInstrumentId
          ? tr('FRONTEND.RECIPIENTS.EDIT.TITLE', 'Recipient details')
          : tr('FRONTEND.RECIPIENTS.CREATE.TITLE', 'Create recipient'),
        content: (
          <WalletRecipientEdit
            walletType={ExternalWalletTypes.JETON_WALLET}
            recipientId={recipient?.paymentInstrumentId}
            currencyCode={currencyCode}
            supportedCountries={supportedCountries}
            onClose={modalHide}
            onSuccess={(response: any) => {
              const { paymentInstrumentId } = response || {}
              reloadRecipients(paymentInstrumentId)
            }}
          />
        )
      })
    }
  }

  const handleInitiateSuccess = (response: unknown): void => {
    if (responseDoesNotContainsChallenge(response)) {
      setResult(response)
      setCurrentStep(3)
    }
  }
  const handleAdvancedInitiate = () => {
    setOtpOnSuccess(() => handleInitiateSuccess)
    handleInitiate(promoCode)
  }

  const hasValidPaymentOption = () => {
    const { paymentOptions } = selectedAccount || {}

    return (
      selectedRecipient?.paymentOption?.status === PaymentOptionStatuses.AVAILABLE ||
      (isValidArray(paymentOptions) && paymentOptions[0].status === PaymentOptionStatuses.AVAILABLE)
    )
  }

  useEffect(() => {
    return () => {
      setCurrentStep(1)
      setResult(null)
      resetMutations()
    }
  }, [])

  return (
    <>
      {currentStep === 1 && (
        <div className={styles['send-money-container']} data-test='jeton-wallet-transfer-container'>
          <div className='from-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.JETON_TRANSFER.FROM', 'From')}</div>
            </div>
            <CorePaymentInput
              selectedOption={selectedAccount}
              setSelectedAccount={setSelectedAccount}
              dataTest={'deposit-payment-input-test'}
              options={accounts}
              enableMaxAmountButton
              paymentOption={selectedRecipient?.paymentOption}
              onChange={(value: any) => formValues.setValue('amount', value)}
              value={formValues.getValue('amount')}
              error={formValues.getError('amount')}
              isLoading={accountsFetching}
            />
          </div>

          <div>
            <div className='to-container'>
              <div className='from-to-label-container'>
                <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.JETON_TRANSFER.TO', 'To')}</div>

                {accountType !== MerchantAccountTypes.PROSPECT && (
                  <MMButton
                    type='button'
                    size='normal'
                    variant='tertiary'
                    leftIcon={RecipientIcon}
                    caption={tr('FRONTEND.RECIPIENTS.LIST.BUTTON_ADD', 'New recipient')}
                    onClick={() => handleEditRecipient()}
                    loading={previewFetching}
                    data-test='button-add-recipient'
                  />
                )}
              </div>

              <CoreSelect
                type='default'
                data-test='destination-payment-instrument-id'
                label={tr('FRONTEND.WITHDRAW.JETON_TRANSFER.RECIPIENT', 'Recipient')}
                options={extendedSelectOptions(filteredRecipients)}
                onChange={handleRecipientChange}
                loading={recipientsFetching}
                disabled={recipientsFetching}
                LeftIcon={<RecipientIcon />}
                value={
                  selectedRecipient
                    ? {
                        value: selectedRecipient?.paymentInstrumentId,
                        label: selectedRecipient?.name
                      }
                    : null
                }
              />
            </div>
          </div>

          {isValidObject(selectedRecipient?.paymentOption) && (
            <SendMoneyMethodInfo
              paymentOption={selectedRecipient.paymentOption}
              currencyCode={selectedAccount?.currencyCode}
            />
          )}

          <CoreInput
            type='text'
            name='reference'
            data-test='reference'
            maxLength={50}
            label={tr('FRONTEND.WITHDRAW.JETON_TRANSFER.REFERENCE', 'Reference')}
            {...formValues.registerInput('reference')}
            autoComplete='off'
            LeftIcon={<ReferenceIcon />}
          />

          <AccountingCategorySelect formValues={formValues} />

          {(shouldShowWarning() || (accountType === MerchantAccountTypes.PROSPECT && !hasValidPaymentOption())) && (
            <CoreOnboardingAnnounce />
          )}
          {shouldPayApplicationFee && <CoreApplicationFeeAnnounce />}

          <div className='form-actions'>
            <ErrorDisplay error={[accountsError, recipientsError, previewError]} />

            <MMButton
              type='button'
              size='large'
              variant='alternative'
              caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
              onClick={handlePreview}
              loading={previewFetching}
              disabled={
                accountsFetching ||
                recipientsFetching ||
                selectedRecipient?.paymentOption?.status !== PaymentOptionStatuses.AVAILABLE ||
                formValues.getFloat('amount') <= 0
              }
              data-test='button-continue'
              fullWidth
            />
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <WalletTransferPreview
          formValues={formValues}
          selectedAccount={selectedAccount}
          selectedRecipient={selectedRecipient}
          onSubmit={handleAdvancedInitiate}
          onCancel={() => setCurrentStep(1)}
          onApplyPromoCode={setPromoCode}
          subFlow={WithdrawalSubFlows.JETON_WALLET}
        />
      )}

      {currentStep === 3 && <SuccessPane response={result} onClick={() => history.push(returnToOriginUrl)} />}
    </>
  )
}

export default JetonWalletTransfer
